import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { HiArrowPath, HiHome, HiLifebuoy } from "react-icons/hi2";

/** react-icons + React 19 JSX typing — same approach as Payment.tsx */
type MaintIcon = React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
const IconRefresh = HiArrowPath as MaintIcon;
const IconHome = HiHome as MaintIcon;
const IconSupport = HiLifebuoy as MaintIcon;

/** Marketing / main OnePlay site (separate from store app root). */
const HOME_URL = process.env.REACT_APP_HOME_URL || "https://oneplay.in";
/**
 * Store app entry (nginx serves maintenance HTML until live, then SPA at this URL).
 * Override if home is not `/`, e.g. `https://store.oneplay.in/` or `/app/`.
 */
const STORE_HOME_URL = process.env.REACT_APP_STORE_HOME_URL?.trim() || "";
/** Optional: public JSON with `{ "message": "..." }` — set in `.env` when you have a status endpoint. */
const STATUS_URL = process.env.REACT_APP_MAINTENANCE_STATUS_URL || "";
const css = `
#op-maint{box-sizing:border-box;min-height:100vh;margin:0;padding:clamp(1rem,4vw,2rem);display:flex;align-items:flex-start;justify-content:center;
  position:relative;overflow:hidden;font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;color:#f1f5f9;-webkit-font-smoothing:antialiased;
  padding-top:clamp(1.25rem,10vh,4rem)}
#op-maint *{box-sizing:border-box}
@media (min-width:1024px){
  #op-maint{align-items:center;padding-top:clamp(1rem,4vw,2rem)}
}
#op-maint .op-bg-stack{position:fixed;inset:0;z-index:0;pointer-events:none}
#op-maint .op-bg-layer{position:absolute;inset:0;background-size:cover;background-position:center;background-repeat:no-repeat;transform:scale(1.02)}
#op-maint .op-bg-mobile{display:block}
#op-maint .op-bg-tablet,#op-maint .op-bg-desktop{display:none}
@media (min-width:768px){
  #op-maint .op-bg-mobile{display:none}
  #op-maint .op-bg-tablet{display:block}
}
@media (min-width:1024px){
  #op-maint .op-bg-tablet{display:none}
  #op-maint .op-bg-desktop{display:block}
}
#op-maint .op-bg-overlay{position:fixed;inset:0;z-index:1;pointer-events:none;
  background:linear-gradient(180deg,rgba(4,2,14,.9) 0%,rgba(8,5,22,.55) 38%,rgba(6,4,18,.72) 70%,rgba(2,1,10,.88) 100%)}
#op-maint .op-bg-overlay::after{content:"";position:absolute;inset:0;pointer-events:none;
  background:radial-gradient(ellipse 95% 75% at 50% 32%,rgba(20,10,45,.45) 0%,transparent 58%),
    radial-gradient(ellipse 80% 60% at 50% 100%,rgba(0,0,0,.5) 0%,transparent 45%)}
#op-maint .op-inner{position:relative;z-index:2;width:100%;max-width:26.5rem;display:flex;flex-direction:column;align-items:stretch;margin-top:0}
#op-maint .card{max-width:26.5rem;width:100%;padding:clamp(1.75rem,5vw,2.35rem) clamp(1.35rem,4vw,2rem);border-radius:20px;text-align:center;
  border:1px solid rgba(255,255,255,.16);background:rgba(6,4,18,.58);
  backdrop-filter:saturate(160%) blur(22px);-webkit-backdrop-filter:saturate(160%) blur(22px);
  box-shadow:0 0 0 1px rgba(255,255,255,.08) inset,0 0 48px rgba(124,58,237,.12),0 28px 90px rgba(0,0,0,.55),0 0 1px rgba(103,232,249,.2)}
#op-maint .logo{display:block;height:clamp(30px,8vw,38px);width:auto;margin:0 auto 1.35rem;filter:drop-shadow(0 4px 24px rgba(124,58,237,.35))}
#op-maint .kicker{margin:0 0 .5rem;font-size:.7rem;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:#94a3b8}
#op-maint h1{margin:0 0 1.1rem;font-size:clamp(1.65rem,5vw,2rem);font-weight:800;line-height:1.15;letter-spacing:-.02em;
  background:linear-gradient(120deg,#fff 0%,#e9d5ff 35%,#67e8f9 90%);-webkit-background-clip:text;background-clip:text;color:transparent}
#op-maint .divider{width:48px;height:3px;margin:0 auto 1.15rem;border-radius:999px;
  background:linear-gradient(90deg,#7c3aed,#22d3ee);opacity:.9}
#op-maint .status{margin:0 0 1.5rem;padding:1rem 1.1rem;border-radius:14px;font-size:.9rem;line-height:1.55;text-align:left;
  color:#cbd5e1;background:rgba(0,0,0,.32);border:1px solid rgba(255,255,255,.1)}
#op-maint .actions{display:grid;grid-template-columns:1fr 1fr;gap:.65rem;width:100%}
@media (max-width:380px){#op-maint .actions{grid-template-columns:1fr}}
#op-maint .btn{min-height:48px;padding:0 1rem;border-radius:14px;font-size:.875rem;font-weight:600;letter-spacing:.01em;cursor:pointer;
  text-decoration:none;display:inline-flex;align-items:center;justify-content:center;gap:.5rem;border:none;font:inherit;width:100%;
  transition:background .2s ease,border-color .2s ease,transform .15s ease,box-shadow .25s ease,color .2s ease}
#op-maint .btn-icon{width:1.125rem;height:1.125rem;flex-shrink:0;opacity:.95}
#op-maint .btn-refresh{color:#f1f5f9;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.2);
  box-shadow:0 1px 0 rgba(255,255,255,.06) inset,0 4px 20px rgba(0,0,0,.2)}
#op-maint .btn-refresh:hover{background:rgba(255,255,255,.14);border-color:rgba(103,232,249,.35);box-shadow:0 0 0 1px rgba(103,232,249,.12),0 8px 28px rgba(0,0,0,.25)}
#op-maint .btn-refresh:hover .btn-icon{animation:op-spin .65s linear}
#op-maint .btn-home{color:#fff;background:linear-gradient(145deg,#8b5cf6 0%,#6d28d9 38%,#0e7490 100%);
  box-shadow:0 8px 32px rgba(109,40,217,.45),0 0 0 1px rgba(255,255,255,.12) inset,0 1px 0 rgba(255,255,255,.2) inset}
#op-maint .btn-home:hover{filter:brightness(1.06);box-shadow:0 12px 40px rgba(14,116,144,.35),0 8px 32px rgba(109,40,217,.5),0 0 0 1px rgba(255,255,255,.15) inset}
#op-maint .btn:active{transform:scale(.98)}
#op-maint .btn:focus-visible{outline:2px solid #67e8f9;outline-offset:3px}
#op-maint .support-wrap{margin-top:1.25rem;display:flex;flex-direction:column;align-items:center;gap:.65rem;width:100%}
#op-maint .btn-support{min-height:44px;padding:0 1.15rem;border-radius:999px;font-size:.8125rem;font-weight:600;cursor:pointer;text-decoration:none;
  display:inline-flex;align-items:center;justify-content:center;gap:.45rem;width:100%;max-width:100%;color:#e2e8f0;
  background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.14);transition:background .2s ease,border-color .2s ease,transform .15s ease,color .2s ease}
#op-maint .btn-support:hover{background:rgba(167,139,250,.12);border-color:rgba(167,139,250,.35);color:#fff}
#op-maint .btn-support .btn-icon{width:1rem;height:1rem;opacity:.9}
#op-maint .btn-support:focus-visible{outline:2px solid #a78bfa;outline-offset:3px}
#op-maint .countdown{margin:0;font-size:.75rem;color:#94a3b8;text-align:center;line-height:1.45}
@keyframes op-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){
  #op-maint .btn{transition:none}
  #op-maint .btn-refresh:hover .btn-icon{animation:none}
}
`;

function statusMessageFromJson(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const m = (data as { message?: unknown }).message;
  return typeof m === "string" && m.trim() ? m.trim() : null;
}

export default function ServerMaintenancePage() {
  const publicUrl = process.env.PUBLIC_URL || "";
  const logoSrc = `${publicUrl}/images/logos/oneplay-logo.svg`;

  /** Responsive art: public/images/maintenance/bg-{mobile,tablet,desktop}.png — optional single URL override for all breakpoints. */
  const maintenanceBackgrounds = useMemo(() => {
    const single = process.env.REACT_APP_MAINTENANCE_BG_URL?.trim();
    const base = `${publicUrl}/images/maintenance`;
    const mobile = single || `${base}/bg-mobile.png`;
    const tablet = single || `${base}/bg-tablet.png`;
    const desktop = single || `${base}/bg-desktop.png`;
    return { mobile, tablet, desktop };
  }, [publicUrl]);

  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);

  const supportHref =
    typeof window !== "undefined" ? `${window.location.origin}/contact-us` : "/contact-us";

  /** Same-origin store home so nginx can return maintenance again until the site is live. */
  const goToStoreHome = useCallback(() => {
    if (typeof window === "undefined") return;
    if (STORE_HOME_URL) {
      window.location.assign(STORE_HOME_URL);
      return;
    }
    window.location.assign(`${window.location.origin}/`);
  }, []);

  useEffect(() => {
    const ms = 30_000 + Math.floor(Math.random() * 30_000);
    const end = Date.now() + ms;
    const tick = () => {
      setSecondsLeft(Math.max(0, Math.ceil((end - Date.now()) / 1000)));
      if (Date.now() >= end) goToStoreHome();
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [goToStoreHome]);

  useEffect(() => {
    if (!STATUS_URL) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(STATUS_URL, { cache: "no-store" });
        if (!res.ok || cancelled) return;
        const msg = statusMessageFromJson(await res.json());
        if (msg && !cancelled) setApiMessage(msg);
      } catch {
        /* ignore */
      }
    };
    poll();
    const id = window.setInterval(poll, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const statusText =
    apiMessage ??
    "We’re updating the OnePlay Store. Your library and purchases are safe — please check back soon.";


  return (
    <>
      <style>{css}</style>
      <Helmet htmlAttributes={{ lang: "en" }}>
        <title>We&apos;ll be back soon | OnePlay Store</title>
        <meta
          name="description"
          content="The OnePlay Store is temporarily unavailable while we perform maintenance."
        />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://store.oneplay.in/maintenance" />
      </Helmet>
      <div id="op-maint">
        <div className="op-bg-stack" aria-hidden="true">
          <div
            className="op-bg-layer op-bg-mobile"
            style={{ backgroundImage: `url(${JSON.stringify(maintenanceBackgrounds.mobile)})` }}
          />
          <div
            className="op-bg-layer op-bg-tablet"
            style={{ backgroundImage: `url(${JSON.stringify(maintenanceBackgrounds.tablet)})` }}
          />
          <div
            className="op-bg-layer op-bg-desktop"
            style={{ backgroundImage: `url(${JSON.stringify(maintenanceBackgrounds.desktop)})` }}
          />
        </div>
        <div className="op-bg-overlay" aria-hidden="true" />
        <div className="op-inner">
          <main className="card" role="main">
            <img className="logo" src={logoSrc} alt="OnePlay" width={160} height={40} decoding="async" />
            <p className="kicker">OnePlay Store</p>
            <h1>We&apos;ll be back soon</h1>
            <div className="divider" aria-hidden="true" />
            <p className="status" role="status" aria-live="polite">
              {statusText}
            </p>
            <div className="actions">
              <button
                type="button"
                className="btn btn-refresh"
                onClick={goToStoreHome}
                aria-label="Try opening the store home page"
              >
                <IconRefresh className="btn-icon" aria-hidden />
                Refresh
              </button>
              <a className="btn btn-home" href={HOME_URL}>
                <IconHome className="btn-icon" aria-hidden />
                Go to Home
              </a>
            </div>
            <div className="support-wrap">
              <a className="btn-support" href={supportHref}>
                <IconSupport className="btn-icon" aria-hidden />
                Support
              </a>
              {secondsLeft !== null ? (
                <p className="countdown" aria-live="polite">
                  Redirecting to store in {secondsLeft}s
                </p>
              ) : null}
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
